/**
 * Firestore collection and segment names for section-scoped analysis data.
 *
 * Layout:
 * `sectionAnalysis/{courseId}/sections/{sectionId}/sectionWeeks/{sectionWeekId}/{leafCollection}/{docId}`
 *
 * Benefits of the `{courseId}` path segment:
 * - Firestore rules can reference `{courseId}` directly without a cross-document
 *   `get()` call on the section doc to resolve the course.
 * - Collection-group queries on leaf collections remain feasible because leaf
 *   collection names are stable.
 */

import type {SectionId, SectionWeekId} from '../../../section/sectionApi';
import type {CourseId} from '../../../types/branded';

/** Root collection for per-section analysis (reflections, debriefs, AI feedback). */
export const SECTION_ANALYSIS_COLLECTION = 'sectionAnalysis' as const;

/**
 * Subcollection under the root that scopes documents to a particular section.
 */
export const SECTION_ANALYSIS_SECTIONS_SUBCOLLECTION = 'sections' as const;

/**
 * Subcollection under a section document holding one logical document id per
 * course week (subcollections may exist even when the week doc has no fields).
 */
export const SECTION_ANALYSIS_SECTION_WEEKS_SUBCOLLECTION =
  'sectionWeeks' as const;

/** Student post-section reflection responses (document id = user id). */
export const SECTION_ANALYSIS_STUDENT_REFLECTIONS_SUBCOLLECTION =
  'studentReflections' as const;

/** Section leader debrief survey responses (document id = user id). */
export const SECTION_ANALYSIS_SL_DEBRIEFS_SUBCOLLECTION = 'slDebriefs' as const;

/** AI-generated SL section feedback documents. */
export const SECTION_ANALYSIS_SL_AI_FEEDBACK_SUBCOLLECTION =
  'slAiFeedback' as const;

/** SL feedback page helpfulness evaluation (document id = user id). */
export const SECTION_ANALYSIS_SL_FEEDBACK_EVALUATION_SUBCOLLECTION =
  'slFeedbackEvaluation' as const;

/** Per-reflection moderation verdicts (document id = same as the reflection doc id, i.e. userId). */
export const SECTION_ANALYSIS_REFLECTION_MODERATION_SUBCOLLECTION =
  'studentReflectionModeration' as const;

/** Peer shadowing feedback (document id = reviewer uid). */
export const SECTION_ANALYSIS_SHADOW_FEEDBACK_SUBCOLLECTION =
  'shadowFeedback' as const;

/** Server-written moderation verdicts for shadowing feedback (document id = reviewer uid). */
export const SECTION_ANALYSIS_SHADOW_FEEDBACK_MODERATION_SUBCOLLECTION =
  'shadowFeedbackModeration' as const;

/** Private flags to course staff on a shadowed section-week, from the reviewer or the reviewee (see `getShadowStaffFlagDocumentPath`). */
export const SECTION_ANALYSIS_SHADOW_STAFF_FLAGS_SUBCOLLECTION =
  'shadowStaffFlags' as const;

/**
 * What the section slides recorded while the SL taught the week (single
 * document). See `sectionLiveSession.ts`.
 */
export const SECTION_ANALYSIS_LIVE_SESSION_SUBCOLLECTION =
  'liveSession' as const;

/** Known leaf collections under `sectionWeeks/{sectionWeekId}`. */
export type SectionAnalysisLeafSubcollection =
  | typeof SECTION_ANALYSIS_STUDENT_REFLECTIONS_SUBCOLLECTION
  | typeof SECTION_ANALYSIS_SL_DEBRIEFS_SUBCOLLECTION
  | typeof SECTION_ANALYSIS_SL_AI_FEEDBACK_SUBCOLLECTION
  | typeof SECTION_ANALYSIS_SL_FEEDBACK_EVALUATION_SUBCOLLECTION
  | typeof SECTION_ANALYSIS_REFLECTION_MODERATION_SUBCOLLECTION
  | typeof SECTION_ANALYSIS_SHADOW_FEEDBACK_SUBCOLLECTION
  | typeof SECTION_ANALYSIS_SHADOW_FEEDBACK_MODERATION_SUBCOLLECTION
  | typeof SECTION_ANALYSIS_SHADOW_STAFF_FLAGS_SUBCOLLECTION
  | typeof SECTION_ANALYSIS_LIVE_SESSION_SUBCOLLECTION;

/**
 * Returns the path to a leaf collection under
 * `sectionAnalysis/{courseId}/sections/{sectionId}/sectionWeeks/{sectionWeek}`.
 */
export function getSectionAnalysisWeekChildCollectionPath({
  courseId,
  sectionId,
  sectionWeek,
  leafSubcollection,
}: {
  courseId: CourseId;
  sectionId: SectionId;
  sectionWeek: SectionWeekId;
  leafSubcollection: SectionAnalysisLeafSubcollection;
}): string {
  return `${SECTION_ANALYSIS_COLLECTION}/${courseId}/${SECTION_ANALYSIS_SECTIONS_SUBCOLLECTION}/${sectionId}/${SECTION_ANALYSIS_SECTION_WEEKS_SUBCOLLECTION}/${String(sectionWeek)}/${leafSubcollection}`;
}
